"use client"
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Button,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import {
  Visibility as VisibilityIcon,
  LocalShipping as ShippingIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
} from "@mui/icons-material"
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined"
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined"
import { OrdenServicio } from "../../types"
import { useAuthStore } from "@/store/auth"
import { useFetchUsuarioResumen } from "@/shared/actions/erp/tickets-soporte-cliente"
import { queryClient } from "@/shared/query-client/query-client"
import ModalEditarTrasladoSupervision from "../supervision-traslado/ModalEditarTrasladoSupervision"
import ModalAuditoriaTraslado from "../supervision-traslado/ModalAuditoriaTraslado"
import { EstadosOrdenServicioEnumChoice, TipoOrdenServicioEnumChoice } from "../../utils/choice.orden-servicio"
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useFetchGetOrdenServicio, useFetchPutOrdenServicio } from "@/shared/actions"
import { formatDateWithTime, ROUTER_PATHS, sanitizeTodos, useLoaders, useTableFilter } from "@/shared"
import {
  useMaterialReactTable,
} from 'material-react-table';
import TablePagination from '@mui/material/TablePagination';
import useModalInfoTrasladoLocalidadStore from "../modal-info-traslado-localidad/useModalInfoTrasladoLocalidadStore"
import ModalInfoTrasladoLocalidad from "../modal-info-traslado-localidad/ModalInfoTrasladoLocalidad"
import { useUiStore } from "@/store/ui"
import { getAgendamientos } from "@/shared/actions/erp"
import { useState } from "react";

// Datos de ejemplo para la maquetacion

const getEstadoColor = (estado: string) => {
  switch (estado.toUpperCase()) {
    case EstadosOrdenServicioEnumChoice.PENDIENTE:
      return "warning"
    case EstadosOrdenServicioEnumChoice.EN_PROCESO:
    case EstadosOrdenServicioEnumChoice.EN_ESPERA:
      return "info"
    case EstadosOrdenServicioEnumChoice.FINALIZADO:
      return "success"
    case EstadosOrdenServicioEnumChoice.CANCELADO:
      return "error"
    default:
      return "default"
  }
}

const getTipoColor = (tipo: string) => {
  switch (tipo.toUpperCase()) {
    case TipoOrdenServicioEnumChoice.TRASLADO:
      return "#1976d2"
    case TipoOrdenServicioEnumChoice.VISITA:
      return "#9c27b0"
    case TipoOrdenServicioEnumChoice.REPARACION:
      return "#ed6c02"
    default:
      return "#757575"
  }
}



interface OrdenServicioCardProps {
  orden: OrdenServicio,
  setOpen: (open: boolean, traslado: any) => void
  handleCancel: (orden: OrdenServicio) => void
  handleTraslado: (traslado: OrdenServicio) => Promise<void>
  canEditar?: boolean
  canAuditoria?: boolean
  onEditar?: (orden: OrdenServicio) => void
  onAuditoria?: (orden: OrdenServicio) => void
}

function OrdenServicioCard({
  orden,
  setOpen,
  handleCancel,
  handleTraslado,
  canEditar,
  canAuditoria,
  onEditar,
  onAuditoria,
}: OrdenServicioCardProps) {
  return (
    <Card
      sx={{
        borderRadius: 1,
        "&:hover": {
          boxShadow: 4,
        },
        transition: "box-shadow 0.2s ease-in-out",
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        {/* Fila 1: Tipo de orden y Fecha de creacion */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: getTipoColor(orden.tipo_orden_servicio),
              }}
            >
              {orden.tipo_orden_servicio}
            </Typography>
            <Chip
              label={orden.estado}
              color={getEstadoColor(orden.estado)}
              size="small"
              sx={{ fontWeight: 500, height: 24 }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
            <CalendarIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2">{formatDateWithTime(orden.fechaCreacion)}</Typography>
          </Box>
        </Box>

        {/* Fila 2: ID Orden, ID Ticket, Contrato */}
        <Box sx={{ display: "flex", gap: 3, mb: 1.5, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Orden:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {orden.numero_orden_servicio || orden.id_orden_servicio.substring(0, 8)}
            </Typography>
          </Box>
          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <TicketIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary">
              Ticket:
            </Typography>
            <Typography variant="body2" fontWeight={500} color="primary">
              {orden.id_ticket}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <DescriptionIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary">
              Contrato:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {orden.contrato}
            </Typography>
          </Box> */}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Fila 3: Usuario, Traslado, Acciones */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            {/* Usuario */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                Creado por:
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {orden.user_create}
              </Typography>
            </Box>

            {/* Traslado */}
            {orden.traslado ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {/* <ShippingIcon sx={{ fontSize: 16, color: "info.main" }} /> */}

                <Button
                  endIcon={<VisibilityIcon fontSize="small" />}
                  size="small" color="primary"
                  onClick={() => setOpen(true, orden)}
                >
                  Ver detalles
                </Button>

                {
                  orden.estado !== EstadosOrdenServicioEnumChoice.CANCELADO && (
                    <Button
                      endIcon={<ShippingIcon fontSize="small" />}
                      size="small" color="primary"
                      onClick={() => handleTraslado(orden)}
                    >
                      Ir a traslado
                    </Button>
                  )
                }

                {/* Supervisión: Editar (solo EN ESPERA) — gateado por permiso */}
                {canEditar && orden.estado === EstadosOrdenServicioEnumChoice.EN_ESPERA && (
                  <Button
                    endIcon={<EditNoteOutlinedIcon fontSize="small" />}
                    size="small" color="secondary"
                    onClick={() => onEditar?.(orden)}
                  >
                    Editar
                  </Button>
                )}

                {/* Supervisión: Auditoría de cambios — gateado por permiso */}
                {canAuditoria && (
                  <Button
                    endIcon={<HistoryOutlinedIcon fontSize="small" />}
                    size="small" color="secondary"
                    onClick={() => onAuditoria?.(orden)}
                  >
                    Auditoría
                  </Button>
                )}

                {/* <Tooltip title="Ver detalles">
                  <IconButton size="small" color="primary">
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip> */}
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ShippingIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                <Typography variant="body2" color="text.disabled">
                  Sin traslado
                </Typography>
              </Box>
            )}
          </Box>

          {/* Acciones */}
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {/* <Tooltip title="Ver detalles">
              <IconButton size="small" color="primary">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip> */}
            {
              orden.estado !== EstadosOrdenServicioEnumChoice.CANCELADO && (
                <Tooltip title="Cancelar">
                  <IconButton size="small" color="error" onClick={() => handleCancel(orden)}>
                    <HighlightOffIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )
            }
          </Box>
        </Box>

        {/* Fecha de modificacion */}
        <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1 }}>
          Ultima modificacion: {formatDateWithTime(orden.fechaModificacion)}
        </Typography>
      </CardContent>
    </Card>
  )
}

type OrdenServicioListProps = {
  tipo_orden_servicio?: string;
  contrato?: string;
  tipo_orden_servicio__exclude_in?: string;
  estado?: string;
  fecha_creacion__gte?: string;
  fecha_creacion__lte?: string;
}

export default function OrdenServicioList({
  tipo_orden_servicio,
  contrato,
  tipo_orden_servicio__exclude_in,
  estado,
  fecha_creacion__gte,
  fecha_creacion__lte,
}: OrdenServicioListProps) {


  const returnUrlAgendamientosPyLPage =
    ROUTER_PATHS.operaciones.trasladosAgendamientoPyLNav;

  const navigate = useNavigate();

  const {
    pagination,
    setPagination
  } = useTableFilter();

  const [loadingAgendamiento, setLoadingAgendamiento] = useState(false)

  const setOpen = useModalInfoTrasladoLocalidadStore(s => s.setOpen)
  const setConfirmDialog = useUiStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiStore(s => s.setConfirmDialogIsOpen);

  // Supervisión de traslados: gateado por PERMISOS de Rol (RBAC tickets-service),
  // gestionables en Gestión de Accesos -> pestaña "Roles". Superusuario bypass.
  const authUser = useAuthStore((s) => s.user);
  const isSuperUser = !!(authUser as any)?.is_superuser;
  const currentUserId = (authUser as any)?.user_id || (authUser as any)?.user || 0;
  const { data: _resumenRes } = useFetchUsuarioResumen(currentUserId, !!currentUserId);
  const _r = _resumenRes as any;
  const userPermisos: string[] =
    _r?.data?.data?.permisos || _r?.data?.permisos || _r?.permisos || [];
  const can = (perm: string) => isSuperUser || userPermisos.includes(perm);
  const canEditar = can("operaciones.editar_traslado_supervision");
  const canAuditoria = can("operaciones.ver_auditoria_traslado");
  const [editarOrden, setEditarOrden] = useState<OrdenServicio | null>(null);
  const [auditoriaOrden, setAuditoriaOrden] = useState<OrdenServicio | null>(null);

  const {
    pageIndex,
    pageSize
  } = pagination;

  // fetch
  const {
    isLoading,
    data,
    isRefetching
  } = useFetchGetOrdenServicio({
    // Cast a any: tipo_orden_servicio__exclude_in es un filtro extra
    // soportado por el backend pero aun no declarado en OrdenServicioParams.
    // Se mantiene el envio para no perder el filtrado server-side.
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      tipo_orden_servicio: sanitizeTodos(tipo_orden_servicio),
      tipo_orden_servicio__exclude_in: tipo_orden_servicio__exclude_in,
      contrato: contrato,
      estado: estado || undefined,
      fecha_creacion__gte: fecha_creacion__gte || undefined,
      fecha_creacion__lte: fecha_creacion__lte || undefined,
    } as any
  });

  const {
    mutateAsync,
    isPending
  } = useFetchPutOrdenServicio()

  // table
  const table = useMaterialReactTable({
    columns: [],
    data: data?.data?.data || [],
    enableTopToolbar: false,
    enableBottomToolbar: false,
  });

  const paginatedRows = table.getRowModel().rows;

  const onCancel = (value: OrdenServicio) => {
    console.log(value);

    setConfirmDialogIsOpen(true);
    setConfirmDialog({
      isOpen: true,
      title: '¡Confirmar!',
      subtitle:
        '¿Seguro que deseas cancelar orden de servicio? Toma en cuenta que esta acción es definitiva y no podrá deshacerse. ¿Deseas continuar?',
      onClose() {
        setConfirmDialogIsOpen(false);
      },
      async onConfirm() {
        setConfirmDialogIsOpen(false);
        await mutateAsync({
          id: value.id_orden_servicio,
          data: {
            ...value,
            estado: EstadosOrdenServicioEnumChoice.CANCELADO
          }
        });
      },
    });
  }


  const handleTraslado = async (traslado: OrdenServicio) => {
    setLoadingAgendamiento(true)

    try {

      const res = await getAgendamientos({
        referencia_orden_servicio: traslado.id_orden_servicio
      })

      if (res.code != 200) {
        toast.error("Error al consultar el Agendamiento")
        return
      }

      const data = res.data

      if (data.count == 0) {
        toast.error("Agendamiento no encontrado")
        return
      }

      navigate(`${returnUrlAgendamientosPyLPage}/${data.data[0].id_agendamiento}`)

    } catch (error) {

    } finally{
      setLoadingAgendamiento(false)

    }
  }

  useLoaders(
    isLoading ||
    isPending ||
    loadingAgendamiento
  )

  if (isRefetching) {
    return <>isRefetching...</>
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 900, mx: "auto", p: 3 }}>
      {/* Lista de cards */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        {paginatedRows.map((row) => (
          <OrdenServicioCard
            key={row.original.id_orden_servicio}
            orden={row.original}
            setOpen={(open, traslado) => {
              setOpen(open, traslado)
            }}
            handleCancel={onCancel}
            handleTraslado={handleTraslado}
            canEditar={canEditar}
            canAuditoria={canAuditoria}
            onEditar={setEditarOrden}
            onAuditoria={setAuditoriaOrden}
          />
        ))}
      </Stack>

      {/* Paginacion */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >

        <TablePagination
          component="div"
          count={data?.data?.count || 0}
          page={pagination.pageIndex}
          rowsPerPage={pagination.pageSize}
          onPageChange={(_, newPage) => {
            setPagination((prev) => ({
              ...prev,
              pageIndex: newPage,
            }));
          }}
          onRowsPerPageChange={(event) => {
            setPagination({
              pageIndex: 0,
              pageSize: parseInt(event.target.value, 10),
            });
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Box>

      {/* Modal "Ver detalles" (store-driven): montado aquí en el componente
          compartido para que funcione tanto en nuevo-ticket como en
          /operaciones/ordenes-servicio. */}
      <ModalInfoTrasladoLocalidad />

      {/* Supervisión: editar traslado (solo con permiso). key = remonta por registro. */}
      {editarOrden && (
        <ModalEditarTrasladoSupervision
          key={`edit-${editarOrden.id_orden_servicio}`}
          open
          orden={editarOrden}
          onClose={() => setEditarOrden(null)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['orden-servicio'] })}
        />
      )}

      {/* Supervisión: auditoría de cambios (solo con permiso). */}
      {auditoriaOrden && (
        <ModalAuditoriaTraslado
          key={`audit-${auditoriaOrden.id_orden_servicio}`}
          open
          orden={auditoriaOrden}
          onClose={() => setAuditoriaOrden(null)}
        />
      )}
    </Box>
  )
}
